import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
    Loader2, Plus, X, Search, BarChart3, AlertCircle, Check, ChevronDown,
    ChevronLeft, ChevronRight, Info,
} from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
    Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { useToast } from '@/hooks/use-toast';
import {
    createEsteiraGroup, updateEsteiraGroup, getEsteiraFormOptions,
    previewEsteiraCount, getFolderTree, listCustomTemplates,
} from '@/services/api';
import type { TemplateFolderNode, CustomTemplate } from '@/types/template-library';
import type {
    EsteiraGroup, EsteiraGroupCreateInput, EsteiraTemplateTypeFilter,
    EsteiraRankingMetric, EsteiraPageTemplateStrategy, EsteiraTemplateTypeMixMode,
} from '@/types/esteira';
import { AUDIENCE_WINDOW_OPTIONS, AUDIENCE_NEEDS_TEMPLATE, type AudienceWindow } from '@/constants/audienceWindow';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    existingGroup?: EsteiraGroup | null;
    onSuccess?: () => void;
}

const COUNTRIES = ['BR', 'CL', 'MX', 'AR', 'PE', 'CO', 'UY', 'PY', 'US', 'ES'];

const DEFAULT_UTMS = {
    utm_source_tpl: 'facebroad_{page_id}',
    utm_medium_tpl: 'grupo_{group_id}',
    utm_campaign_tpl: 'broad_{broadcast_id}',
    utm_content_tpl: 'grupo{group_id}_{HH:MM}',
};

const PLACEHOLDER_HINT = '{page_id} {group_id} {broadcast_id} {HH:MM} {date} {country}';

// Fuso do grupo. 'auto' = backend deriva pela MAIORIA das páginas do grupo
// (ignora páginas sem geolocalização; fallback São Paulo). Valores IANA cobrem
// os países realmente usados na plataforma.
const TZ_AUTO = 'auto';
const TIMEZONE_OPTIONS: { value: string; label: string }[] = [
    { value: 'America/Sao_Paulo', label: 'Brasil — São Paulo (UTC−3)' },
    { value: 'America/Bahia', label: 'Brasil — Bahia (UTC−3)' },
    { value: 'America/New_York', label: 'EUA — Leste / New York (ET)' },
    { value: 'America/Chicago', label: 'EUA — Central / Chicago (CT)' },
    { value: 'America/Denver', label: 'EUA — Montanha / Denver (MT)' },
    { value: 'America/Los_Angeles', label: 'EUA — Pacífico / Los Angeles (PT)' },
    { value: 'America/Mexico_City', label: 'México — Cidade do México' },
    { value: 'America/Lima', label: 'Peru — Lima (UTC−5)' },
    { value: 'America/Bogota', label: 'Colômbia — Bogotá (UTC−5)' },
    { value: 'America/Santiago', label: 'Chile — Santiago' },
    { value: 'America/Argentina/Buenos_Aires', label: 'Argentina — Buenos Aires (UTC−3)' },
    { value: 'America/Montevideo', label: 'Uruguai — Montevidéu (UTC−3)' },
    { value: 'America/Asuncion', label: 'Paraguai — Assunção' },
    { value: 'Europe/Madrid', label: 'Espanha — Madri (CET)' },
    { value: 'Europe/London', label: 'Reino Unido — Londres (GMT)' },
    { value: 'Australia/Sydney', label: 'Austrália — Sydney' },
    { value: 'UTC', label: 'UTC' },
];

type Step = 1 | 2 | 3 | 4;
const STEP_LABELS: Record<Step, string> = {
    1: 'Identificação',
    2: 'Conteúdo',
    3: 'Configurações',
    4: 'Tracking',
};

export default function EsteiraGroupFormDialog({
    open, onOpenChange, existingGroup, onSuccess,
}: Props) {
    const { toast } = useToast();
    const isEdit = !!existingGroup;

    // Wizard state
    const [step, setStep] = useState<Step>(1);

    // ─── Form state ─────────────────────────────────────────────────
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [country, setCountry] = useState('BR');
    const [templateTypeFilter, setTemplateTypeFilter] = useState<EsteiraTemplateTypeFilter>('any');

    const [projectFilter, setProjectFilter] = useState<number[]>([]);
    const [sourceDomains, setSourceDomains] = useState<string[]>([]);
    const [pageIds, setPageIds] = useState<number[]>([]);
    const [domainSearch, setDomainSearch] = useState('');
    const [pageSearch, setPageSearch] = useState('');

    const [scheduleTimes, setScheduleTimes] = useState<string[]>(['08:00', '12:00', '18:00']);
    const [newTime, setNewTime] = useState('');
    // Fuso do grupo: 'auto' = maioria das páginas (fallback); senão IANA explícito.
    const [scheduleTimezone, setScheduleTimezone] = useState<string>(TZ_AUTO);
    const [rankingMetric, setRankingMetric] = useState<EsteiraRankingMetric>('ctr');
    const [rankingPeriodDays, setRankingPeriodDays] = useState(30);
    const [useAllTimeRanking, setUseAllTimeRanking] = useState(false);
    const [topPoolSize, setTopPoolSize] = useState(50);
    const [minTotalSent, setMinTotalSent] = useState(0);
    const [minPageviews, setMinPageviews] = useState(0);
    const [antiRepeatWindow, setAntiRepeatWindow] = useState(10);
    const [contentRepeatWindow, setContentRepeatWindow] = useState(5);
    // IA removida da UI (vai ser implementada no futuro). Backend ainda aceita,
    // então enviamos defaults: ai_enabled=false, ai_frequency_pct=0.

    // Distribuição + mix sequencial
    const [pageTemplateStrategy, setPageTemplateStrategy] =
        useState<EsteiraPageTemplateStrategy>('synchronized');
    const [templateTypeMixMode, setTemplateTypeMixMode] =
        useState<EsteiraTemplateTypeMixMode>('filter_only');
    const [cardsPerButton, setCardsPerButton] = useState<number>(3);

    // ─── Pastas da Biblioteca (modo híbrido) ───────────────────────
    // mix_pct = 0   → 100% Template Intelligence (legado/default)
    // mix_pct = 100 → 100% pastas próprias (TI ignorado)
    // mix_pct 1-99  → mix por sorteio em cada slot
    const [folderIds, setFolderIds] = useState<number[]>([]);
    const [includeSubfolders, setIncludeSubfolders] = useState(true);
    const [folderMixPct, setFolderMixPct] = useState<number>(0);
    const [folderPickerOpen, setFolderPickerOpen] = useState(false);

    // Fallback de envio. Pasta de templates UTILITY aprovados usada quando o
    // envio primário raw falha (#10 fora da janela). Liberado pra todos.
    const [fallbackFolderId, setFallbackFolderId] = useState<number | null>(null);
    const [fallbackPickerOpen, setFallbackPickerOpen] = useState(false);
    const [foldersPreviewOpen, setFoldersPreviewOpen] = useState(false);

    // ─── Sync Meta + retry ─────────────────────────────────────────
    const [metaSyncEnabled, setMetaSyncEnabled] = useState(true);
    // CSV pra input livre. Split/join no submit/hydrate.
    const [retryErrorCodesRaw, setRetryErrorCodesRaw] = useState<string>('');
    const [retryOffsetMinutes, setRetryOffsetMinutes] = useState<number | null>(null);

    // ─── Segmentação de audiência ──────────────────────────────────
    const [audienceWindow, setAudienceWindow] =
        useState<AudienceWindow>('all');
    const [skipLeadsInFlow, setSkipLeadsInFlow] = useState(false);

    // Tree de pastas (carrega quando o user habilita modo pasta)
    // Usa o 1º projeto do filtro (se houver) — alinha com a Biblioteca de
    // modelos, que é single-project. Sem projeto = pessoal (user-only).
    const folderProjectId = projectFilter[0] ?? null;
    const { data: folderTree } = useQuery({
        queryKey: ['esteira-folder-tree', folderProjectId],
        queryFn: () => getFolderTree(folderProjectId),
        // Carrega sempre que o dialog abre — usada pelo modo pasta (primário) e
        // pelo picker de fallback (liberado pra todos).
        enabled: open,
        staleTime: 1000 * 60 * 2,
    });

    // Lista flat de pastas pra picker (com contagem de templates)
    const flatFolders: Array<TemplateFolderNode & { template_count: number }> = useMemo(() => {
        if (!folderTree?.folders) return [];
        const flat: Array<TemplateFolderNode & { template_count: number }> = [];
        const walk = (nodes: TemplateFolderNode[], depth = 0) => {
            for (const n of nodes) {
                flat.push({ ...n, template_count: n.template_count ?? 0 });
                if (n.children?.length) walk(n.children, depth + 1);
            }
        };
        walk(folderTree.folders);
        return flat;
    }, [folderTree]);

    // Templates da(s) pasta(s) selecionada(s) — usado pelo dialog "Ver templates"
    const { data: previewTemplates = [] as CustomTemplate[] } = useQuery<CustomTemplate[]>({
        queryKey: ['esteira-folder-preview', folderProjectId, folderIds],
        queryFn: async () => {
            if (!folderIds.length) return [] as CustomTemplate[];
            // Pega templates do primeiro folder (preview rápido — apenas pra inspeção)
            const r = await listCustomTemplates(folderProjectId, folderIds[0]);
            return (r?.items ?? []) as CustomTemplate[];
        },
        enabled: foldersPreviewOpen && folderIds.length > 0,
        staleTime: 1000 * 30,
    });

    // URL de destino única — sobrescreve foto + todos os botões do template.
    // Backend ainda aceita array (compat), mandamos como [destinationUrl].
    const [destinationUrl, setDestinationUrl] = useState('');
    const [utmSourceTpl, setUtmSourceTpl] = useState(DEFAULT_UTMS.utm_source_tpl);
    const [utmMediumTpl, setUtmMediumTpl] = useState(DEFAULT_UTMS.utm_medium_tpl);
    const [utmCampaignTpl, setUtmCampaignTpl] = useState(DEFAULT_UTMS.utm_campaign_tpl);
    const [utmContentTpl, setUtmContentTpl] = useState(DEFAULT_UTMS.utm_content_tpl);

    // Multi-select de projetos: popover state
    const [projectsPopoverOpen, setProjectsPopoverOpen] = useState(false);

    // Step 2 accordion + filtros adicionais de páginas
    const [domainSectionOpen, setDomainSectionOpen] = useState(true);
    const [pageSectionOpen, setPageSectionOpen] = useState(false);
    const pageSectionAutoOpened = useRef(false);
    const [categoryFilter, setCategoryFilter] = useState<number[]>([]);
    const [metaUserFilter, setMetaUserFilter] = useState<string[]>([]);
    const [categoryPopoverOpen, setCategoryPopoverOpen] = useState(false);
    const [metaUserPopoverOpen, setMetaUserPopoverOpen] = useState(false);

    // Carrega opções
    const { data: formOptions, isLoading: optionsLoading } = useQuery({
        queryKey: ['esteira-form-options'],
        queryFn: getEsteiraFormOptions,
        enabled: open,
        staleTime: 1000 * 60 * 5,
    });

    const projects = formOptions?.projects ?? [];
    const allDomains = formOptions?.domains ?? [];
    const allPages = formOptions?.pages ?? [];
    const allCategories = formOptions?.categories ?? [];

    // Lista única de usuários Meta nas pages visíveis (pro filtro)
    const allMetaUsers = useMemo(() => {
        const set = new Set<string>();
        for (const p of allPages) {
            if (p.meta_user_name) set.add(p.meta_user_name);
        }
        return Array.from(set).sort();
    }, [allPages]);

    // Reset/load ao abrir
    useEffect(() => {
        if (!open) return;
        setStep(1);
        if (existingGroup) {
            setName(existingGroup.name);
            setDescription(existingGroup.description ?? '');
            setCountry(existingGroup.target_country);
            setSourceDomains(existingGroup.source_domains || []);
            setPageIds(existingGroup.page_ids || []);
            setScheduleTimes(existingGroup.schedule_times || []);
            setScheduleTimezone(existingGroup.schedule_timezone || TZ_AUTO);
            setTemplateTypeFilter(existingGroup.template_type_filter);
            setRankingMetric(existingGroup.ranking_metric);
            setRankingPeriodDays(existingGroup.ranking_period_days);
            setUseAllTimeRanking(existingGroup.use_all_time_ranking ?? false);
            setTopPoolSize(existingGroup.top_pool_size);
            setMinTotalSent(existingGroup.min_total_sent ?? 0);
            setMinPageviews(existingGroup.min_pageviews ?? 0);
            setAntiRepeatWindow(existingGroup.anti_repeat_window);
            setContentRepeatWindow(existingGroup.content_repeat_window ?? 5);
            setPageTemplateStrategy(existingGroup.page_template_strategy ?? 'synchronized');
            setTemplateTypeMixMode(existingGroup.template_type_mix_mode ?? 'filter_only');
            setCardsPerButton(existingGroup.cards_per_button ?? 3);
            // Existing pode ter múltiplas URLs (legado) — pegamos a primeira como destino único
            setDestinationUrl((existingGroup.url_overrides_base && existingGroup.url_overrides_base[0]) || '');
            setUtmSourceTpl(existingGroup.utm_source_tpl);
            setUtmMediumTpl(existingGroup.utm_medium_tpl);
            setUtmCampaignTpl(existingGroup.utm_campaign_tpl);
            setUtmContentTpl(existingGroup.utm_content_tpl);
            setFolderIds(existingGroup.template_folder_ids ?? []);
            setFallbackFolderId(existingGroup.fallback_folder_id ?? null);
            setIncludeSubfolders(existingGroup.template_folder_include_subfolders ?? true);
            setFolderMixPct(existingGroup.template_folder_mix_pct ?? 0);
            setMetaSyncEnabled((existingGroup as { meta_sync_enabled?: boolean }).meta_sync_enabled ?? true);
            const codes = (existingGroup as { retry_error_codes?: string[] }).retry_error_codes;
            setRetryErrorCodesRaw(Array.isArray(codes) ? codes.join(',') : '');
            setRetryOffsetMinutes((existingGroup as { retry_error_offset_minutes?: number | null }).retry_error_offset_minutes ?? null);
            setAudienceWindow((existingGroup as { audience_window?: AudienceWindow }).audience_window ?? 'all');
            setSkipLeadsInFlow((existingGroup as { skip_leads_in_flow?: boolean }).skip_leads_in_flow ?? false);
        } else {
            setName('');
            setDescription('');
            setCountry('BR');
            setSourceDomains([]);
            setPageIds([]);
            setScheduleTimes(['08:00', '12:00', '18:00']);
            setScheduleTimezone(TZ_AUTO);
            setTemplateTypeFilter('any');
            setRankingMetric('ctr');
            setRankingPeriodDays(30);
            setUseAllTimeRanking(false);
            setTopPoolSize(50);
            setMinTotalSent(0);
            setMinPageviews(0);
            setAntiRepeatWindow(10);
            setContentRepeatWindow(5);
            setPageTemplateStrategy('synchronized');
            setTemplateTypeMixMode('filter_only');
            setCardsPerButton(3);
            setUtmSourceTpl(DEFAULT_UTMS.utm_source_tpl);
            setUtmMediumTpl(DEFAULT_UTMS.utm_medium_tpl);
            setUtmCampaignTpl(DEFAULT_UTMS.utm_campaign_tpl);
            setUtmContentTpl(DEFAULT_UTMS.utm_content_tpl);
            setDestinationUrl('');
            setFolderIds([]);
            setFallbackFolderId(null);
            setIncludeSubfolders(true);
            setFolderMixPct(0);
            setMetaSyncEnabled(true);
            setRetryErrorCodesRaw('10,551');
            setRetryOffsetMinutes(45);
            setAudienceWindow('all');
            setSkipLeadsInFlow(false);
        }
        setProjectFilter([]);
        setDomainSearch('');
        setPageSearch('');
        setNewTime('');
        setCategoryFilter([]);
        setMetaUserFilter([]);
        // Accordion: começa com domínios aberto, páginas fechado
        setDomainSectionOpen(true);
        // Se edit e já tem domínios, abre página direto
        const hasDomains = !!(existingGroup?.source_domains?.length);
        setPageSectionOpen(hasDomains);
        pageSectionAutoOpened.current = hasDomains;
    }, [open, existingGroup]);

    // ─── Preview count debounced (mostrado no step 2) ────────────────
    const [previewCount, setPreviewCount] = useState<{ total: number; per_domain: Record<string, number> } | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    useEffect(() => {
        if (!open || sourceDomains.length === 0) {
            setPreviewCount(null);
            return;
        }
        setPreviewLoading(true);
        const handle = setTimeout(async () => {
            try {
                const r = await previewEsteiraCount({
                    source_domains: sourceDomains,
                    target_country: country,
                    template_type_filter: templateTypeFilter,
                    min_total_sent: minTotalSent,
                    min_pageviews: minPageviews,
                    ranking_metric: rankingMetric,
                    ranking_period_days: rankingPeriodDays,
                });
                setPreviewCount(r);
            } catch {
                setPreviewCount(null);
            } finally {
                setPreviewLoading(false);
            }
        }, 500);
        return () => clearTimeout(handle);
    }, [open, sourceDomains, country, templateTypeFilter, minTotalSent, minPageviews, rankingMetric, rankingPeriodDays]);

    // ─── Domínios/Páginas filtrados (por projeto + busca) ────────────
    const visibleDomains = useMemo(() => {
        const filtered = projectFilter.length === 0
            ? allDomains
            : allDomains.filter((d) =>
                d.project_id != null && projectFilter.includes(d.project_id),
              );
        const q = domainSearch.trim().toLowerCase();
        return q ? filtered.filter((d) => d.domain?.toLowerCase().includes(q)) : filtered;
    }, [allDomains, projectFilter, domainSearch]);

    const visiblePages = useMemo(() => {
        let filtered = projectFilter.length === 0
            ? allPages
            : allPages.filter((p) =>
                p.project_ids.some((pid) => projectFilter.includes(pid)),
              );
        if (categoryFilter.length > 0) {
            filtered = filtered.filter((p) =>
                p.category_id != null && categoryFilter.includes(p.category_id),
            );
        }
        if (metaUserFilter.length > 0) {
            filtered = filtered.filter((p) =>
                p.meta_user_name != null && metaUserFilter.includes(p.meta_user_name),
            );
        }
        const q = pageSearch.trim().toLowerCase();
        return q
            ? filtered.filter((p) =>
                p.page_name?.toLowerCase().includes(q)
                || p.meta_user_name?.toLowerCase().includes(q)
                || p.page_id.includes(q),
              )
            : filtered;
    }, [allPages, projectFilter, categoryFilter, metaUserFilter, pageSearch]);

    // Auto-expand seção de páginas na primeira vez que seleciona 1+ domínio
    useEffect(() => {
        if (sourceDomains.length > 0 && !pageSectionAutoOpened.current) {
            pageSectionAutoOpened.current = true;
            setDomainSectionOpen(false);
            setPageSectionOpen(true);
        }
    }, [sourceDomains.length]);

    // ─── Toggles ─────────────────────────────────────────────────────
    const toggleProjectFilter = (id: number) => {
        setProjectFilter((prev) =>
            prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
        );
    };
    const toggleDomain = (d: string) => {
        setSourceDomains((prev) =>
            prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d],
        );
    };
    const togglePage = (id: number) => {
        setPageIds((prev) =>
            prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
        );
    };
    const addTime = () => {
        const t = newTime.trim();
        if (!/^\d{2}:\d{2}$/.test(t) || scheduleTimes.includes(t)) return;
        const [h, m] = t.split(':').map(Number);
        if (h < 0 || h > 23 || m < 0 || m > 59) return;
        setScheduleTimes([...scheduleTimes, t].sort());
        setNewTime('');
    };
    const removeTime = (t: string) => setScheduleTimes(scheduleTimes.filter((x) => x !== t));

    // Valida URL absoluta http(s) — usado pela validação do step 4
    const isValidUrl = (s: string) => {
        const u = s.trim();
        if (!u) return false;
        try {
            const parsed = new URL(u);
            return parsed.protocol === 'http:' || parsed.protocol === 'https:';
        } catch {
            return false;
        }
    };

    // ─── Validação por step ──────────────────────────────────────────
    const step1Valid = name.trim().length > 0 && country.length === 2;
    // Step 2: precisa de páginas + fonte de templates válida (domínios OU pastas
    // conforme o folderMixPct). 0% → exige domínios. 100% → exige pastas.
    // 1-99% (híbrido) → exige ambos.
    const hasSourceMaterial = (() => {
        if (folderMixPct === 0) return sourceDomains.length > 0;
        if (folderMixPct === 100) return folderIds.length > 0;
        return sourceDomains.length > 0 && folderIds.length > 0;
    })();
    const step2Valid = hasSourceMaterial && pageIds.length > 0;
    const step3Valid = scheduleTimes.length > 0 && (
        templateTypeMixMode !== 'sequential'
        || (templateTypeFilter === 'any' && cardsPerButton >= 1)
    );
    const step4Valid = isValidUrl(destinationUrl);  // URL de destino obrigatória
    const allValid = step1Valid && step2Valid && step3Valid && step4Valid;

    const canGoNext = (() => {
        if (step === 1) return step1Valid;
        if (step === 2) return step2Valid;
        if (step === 3) return step3Valid;
        return step4Valid;  // último step: botão "Salvar" só habilita se URL ok
    })();

    // ─── Submit ──────────────────────────────────────────────────────
    const submitMutation = useMutation({
        mutationFn: async () => {
            const payload: EsteiraGroupCreateInput = {
                name: name.trim(),
                description: description.trim() || undefined,
                target_country: country,
                source_domains: sourceDomains,
                template_type_filter: templateTypeFilter,
                page_ids: pageIds,
                schedule_times: scheduleTimes,
                // 'auto' → backend deriva pela maioria das páginas; senão IANA explícito.
                schedule_timezone: scheduleTimezone,
                ranking_metric: rankingMetric,
                ranking_period_days: rankingPeriodDays,
                use_all_time_ranking: useAllTimeRanking,
                top_pool_size: topPoolSize,
                min_total_sent: minTotalSent,
                min_pageviews: minPageviews,
                anti_repeat_window: antiRepeatWindow,
                content_repeat_window: contentRepeatWindow,
                page_template_strategy: pageTemplateStrategy,
                template_type_mix_mode: templateTypeMixMode,
                cards_per_button: templateTypeMixMode === 'sequential' ? cardsPerButton : null,
                ai_enabled: false,
                ai_frequency_pct: 0,
                // Array de 1 elemento — backend template-intel aplica a MESMA URL em todos
                // os botões + na foto (default_action herda do primeiro botão renderizado).
                url_overrides_base: destinationUrl.trim() ? [destinationUrl.trim()] : null,
                utm_source_tpl: utmSourceTpl,
                utm_medium_tpl: utmMediumTpl,
                utm_campaign_tpl: utmCampaignTpl,
                utm_content_tpl: utmContentTpl,
                // Hardcoded — esteira sempre usa UTILITY pra contornar janela 24h
                message_tag: 'ACCOUNT_UPDATE',
                // Pastas da Biblioteca (modo híbrido)
                template_folder_ids: folderMixPct > 0 ? folderIds : null,
                template_folder_include_subfolders: includeSubfolders,
                template_folder_mix_pct: folderMixPct,
                // Fallback (liberado pra todos)
                fallback_folder_id: fallbackFolderId,
                // Sync Meta + retry
                meta_sync_enabled: metaSyncEnabled,
                retry_error_codes: retryErrorCodesRaw.trim()
                    ? retryErrorCodesRaw.split(',').map((s) => s.trim()).filter(Boolean)
                    : null,
                retry_error_offset_minutes: retryOffsetMinutes ?? null,
                // Segmentação de audiência
                audience_window: audienceWindow,
                skip_leads_in_flow: skipLeadsInFlow,
            };
            if (isEdit && existingGroup) {
                return updateEsteiraGroup(existingGroup.id, payload);
            }
            return createEsteiraGroup(payload);
        },
        onSuccess: () => {
            toast({
                title: isEdit ? 'Grupo atualizado' : 'Grupo criado',
                description: isEdit
                    ? 'Mudanças aplicadas.'
                    : 'Worker vai preencher a fila nos próximos 5 minutos.',
            });
            onSuccess?.();
            onOpenChange(false);
        },
        onError: (e: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro',
                description: e?.response?.data?.detail || e?.message || 'Falha ao salvar.',
            });
        },
    });

    // ─── Botão X explícito ───────────────────────────────────────────
    const handleClose = () => onOpenChange(false);

    return (
        <Dialog
            open={open}
            // Não fechamos por click fora ou ESC — só pelo botão X
            onOpenChange={(o) => {
                // Permitir que componentes filhos abram/fechem o dialog normalmente.
                // Mas pra "fechar o modal inteiro" exigir o botão X explícito,
                // ignoramos o close vindo do overlay/ESC. Submit/cancel chamam onOpenChange direto.
                if (o) onOpenChange(true);
            }}
        >
            <DialogContent
                className="sm:max-w-[820px] max-h-[92vh] overflow-hidden flex flex-col p-0 [&>button.absolute]:hidden"
                onPointerDownOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
            >
                {/* Header com X explícito */}
                <DialogHeader className="px-6 pt-6 pb-3 border-b">
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <DialogTitle>
                                {isEdit ? `Editar grupo · ${existingGroup?.name}` : 'Novo grupo de esteira'}
                            </DialogTitle>
                            <DialogDescription className="mt-1 text-xs">
                                Backend mantém 7 dias de agendamento à frente automaticamente.
                            </DialogDescription>
                        </div>
                        <button
                            type="button"
                            onClick={handleClose}
                            className="rounded-md hover:bg-gray-100 p-2 transition-colors"
                            aria-label="Fechar"
                        >
                            <X className="w-4 h-4 text-gray-500" />
                        </button>
                    </div>

                    {/* Stepper */}
                    <div className="mt-4 flex items-center gap-2">
                        {([1, 2, 3, 4] as Step[]).map((s, idx) => (
                            <div key={s} className="flex items-center gap-2 flex-1">
                                <div
                                    onClick={() => {
                                        // Permite voltar pra steps anteriores; ir pra frente exige
                                        // passar pelo botão "Próximo" (que valida)
                                        if (s < step) setStep(s);
                                    }}
                                    className={`flex items-center gap-2 cursor-pointer ${s > step ? 'opacity-40' : ''}`}
                                >
                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                                        s < step ? 'bg-emerald-100 text-emerald-700' :
                                        s === step ? 'bg-violet-600 text-white' :
                                        'bg-gray-100 text-gray-400'
                                    }`}>
                                        {s < step ? <Check className="w-3.5 h-3.5" /> : s}
                                    </div>
                                    <span className={`text-xs ${s === step ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>
                                        {STEP_LABELS[s]}
                                    </span>
                                </div>
                                {idx < 3 && <div className={`flex-1 h-px ${s < step ? 'bg-emerald-300' : 'bg-gray-200'}`} />}
                            </div>
                        ))}
                    </div>
                </DialogHeader>

                {/* Conteúdo do step atual */}
                <div className="flex-1 overflow-y-auto px-6 py-5">
                    {step === 1 && (
                        <div className="grid gap-5">
                            <div className="grid gap-2">
                                <Label htmlFor="esteira-name">Nome do grupo *</Label>
                                <Input
                                    id="esteira-name" value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Ex: Carros Chile - app.twintnews"
                                />
                            </div>
                            <div className="grid gap-2">
                                <Label htmlFor="esteira-desc">Descrição</Label>
                                <Textarea
                                    id="esteira-desc" value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Opcional — propósito do grupo"
                                    rows={3}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="grid gap-2">
                                    <Label>País alvo *</Label>
                                    <Select value={country} onValueChange={setCountry}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {COUNTRIES.map((c) => (
                                                <SelectItem key={c} value={c}>{c}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="grid gap-2">
                                    <Label>Tipo de template</Label>
                                    <Select
                                        value={templateTypeFilter}
                                        onValueChange={(v) => setTemplateTypeFilter(v as EsteiraTemplateTypeFilter)}
                                    >
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="any">Qualquer</SelectItem>
                                            <SelectItem value="generic">Card (imagem + botões)</SelectItem>
                                            <SelectItem value="button">Texto + botões</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="grid gap-5">
                            {/* Filtro projeto — multi-select dropdown */}
                            <div className="grid gap-2">
                                <Label>Filtrar por projeto</Label>
                                <Popover open={projectsPopoverOpen} onOpenChange={setProjectsPopoverOpen}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            role="combobox"
                                            className="w-full justify-between font-normal"
                                        >
                                            <span className="truncate">
                                                {projectFilter.length === 0
                                                    ? 'Todos os projetos'
                                                    : `${projectFilter.length} projeto${projectFilter.length > 1 ? 's' : ''} selecionado${projectFilter.length > 1 ? 's' : ''}`}
                                            </span>
                                            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        className="p-0"
                                        style={{ width: 'var(--radix-popover-trigger-width)' }}
                                        align="start"
                                    >
                                        <Command>
                                            <CommandInput placeholder="Buscar projeto..." />
                                            <CommandList>
                                                <CommandEmpty>Nenhum projeto encontrado.</CommandEmpty>
                                                <CommandGroup>
                                                    {projects.map((p) => {
                                                        const selected = projectFilter.includes(p.id);
                                                        return (
                                                            <CommandItem
                                                                key={p.id}
                                                                value={p.name}
                                                                onSelect={() => toggleProjectFilter(p.id)}
                                                            >
                                                                <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${
                                                                    selected ? 'bg-violet-600 border-violet-600' : 'border-gray-300'
                                                                }`}>
                                                                    {selected && <Check className="h-3 w-3 text-white" />}
                                                                </div>
                                                                <span>{p.name}</span>
                                                            </CommandItem>
                                                        );
                                                    })}
                                                </CommandGroup>
                                                {projectFilter.length > 0 && (
                                                    <div className="border-t p-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => setProjectFilter([])}
                                                            className="text-xs text-gray-500 hover:underline w-full text-left px-2 py-1"
                                                        >
                                                            Limpar seleção
                                                        </button>
                                                    </div>
                                                )}
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                                {projectFilter.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {projectFilter.map((pid) => {
                                            const proj = projects.find((p) => p.id === pid);
                                            return (
                                                <Badge key={pid} variant="outline" className="gap-1">
                                                    {proj?.name ?? `#${pid}`}
                                                    <button onClick={() => toggleProjectFilter(pid)} className="ml-1 text-red-500">
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </Badge>
                                            );
                                        })}
                                    </div>
                                )}
                                <p className="text-[10px] text-gray-400 mt-0.5">
                                    Filtra apenas a EXIBIÇÃO da lista de domínios/páginas abaixo.
                                </p>
                            </div>

                            {/* ─── Pastas da Biblioteca (modo híbrido) ─── */}
                            <div className="border rounded-lg overflow-hidden bg-violet-50/30">
                                <div className="px-3 py-3 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-violet-900">
                                                📁 Pastas da Biblioteca
                                            </span>
                                            <Badge variant="outline" className="bg-white">
                                                {folderMixPct === 0 && 'desligado'}
                                                {folderMixPct === 100 && '100% pastas'}
                                                {folderMixPct > 0 && folderMixPct < 100 && `${folderMixPct}% mix`}
                                            </Badge>
                                        </div>
                                        {folderIds.length > 0 && (
                                            <button
                                                type="button"
                                                onClick={() => setFoldersPreviewOpen(true)}
                                                className="text-xs text-violet-700 hover:underline"
                                            >
                                                Ver templates
                                            </button>
                                        )}
                                    </div>

                                    {/* Slider mix_pct */}
                                    <div className="grid gap-1.5">
                                        <div className="flex items-center justify-between text-xs">
                                            <Label className="text-xs">Proporção do envio que vem das pastas</Label>
                                            <span className="font-mono text-violet-700 font-semibold">
                                                {folderMixPct}%
                                            </span>
                                        </div>
                                        <input
                                            type="range"
                                            min={0}
                                            max={100}
                                            step={5}
                                            value={folderMixPct}
                                            onChange={(e) => setFolderMixPct(Number(e.target.value))}
                                            className="w-full accent-violet-600"
                                        />
                                        <p className="text-[10px] text-gray-500 leading-tight">
                                            <strong>0%</strong>: só Template Intelligence (modo atual).{' '}
                                            <strong>100%</strong>: só pastas próprias (TI ignorado).{' '}
                                            <strong>1-99%</strong>: mistura por sorteio em cada envio.
                                        </p>
                                    </div>

                                    {folderMixPct > 0 && (
                                        <>
                                            {/* Seletor de pastas */}
                                            <div className="grid gap-1.5">
                                                <Label className="text-xs">
                                                    Pastas selecionadas <span className="text-red-500">*</span>
                                                </Label>
                                                <Popover open={folderPickerOpen} onOpenChange={setFolderPickerOpen}>
                                                    <PopoverTrigger asChild>
                                                        <Button
                                                            variant="outline"
                                                            role="combobox"
                                                            className="w-full justify-between font-normal bg-white"
                                                        >
                                                            <span className="truncate">
                                                                {folderIds.length === 0
                                                                    ? 'Nenhuma pasta selecionada'
                                                                    : `${folderIds.length} pasta${folderIds.length > 1 ? 's' : ''} selecionada${folderIds.length > 1 ? 's' : ''}`}
                                                            </span>
                                                            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                                        </Button>
                                                    </PopoverTrigger>
                                                    <PopoverContent
                                                        className="p-0"
                                                        style={{ width: 'var(--radix-popover-trigger-width)' }}
                                                        align="start"
                                                    >
                                                        <Command>
                                                            <CommandInput placeholder="Buscar pasta..." />
                                                            <CommandList className="max-h-[320px] overflow-y-auto">
                                                                <CommandEmpty>
                                                                    Nenhuma pasta criada ainda — vá em "Biblioteca de modelos".
                                                                </CommandEmpty>
                                                                <CommandGroup>
                                                                    {flatFolders.map((f) => {
                                                                        const selected = folderIds.includes(f.id);
                                                                        return (
                                                                            <CommandItem
                                                                                key={f.id}
                                                                                value={f.name}
                                                                                onSelect={() =>
                                                                                    setFolderIds((prev) =>
                                                                                        selected
                                                                                            ? prev.filter((x) => x !== f.id)
                                                                                            : [...prev, f.id]
                                                                                    )
                                                                                }
                                                                            >
                                                                                <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${
                                                                                    selected ? 'bg-violet-600 border-violet-600' : 'border-gray-300'
                                                                                }`}>
                                                                                    {selected && <Check className="h-3 w-3 text-white" />}
                                                                                </div>
                                                                                <span className="flex-1 truncate">{f.name}</span>
                                                                                <span className="text-[10px] text-gray-400 ml-2">
                                                                                    {f.template_count} {f.template_count === 1 ? 'tpl' : 'tpls'}
                                                                                </span>
                                                                            </CommandItem>
                                                                        );
                                                                    })}
                                                                </CommandGroup>
                                                            </CommandList>
                                                        </Command>
                                                    </PopoverContent>
                                                </Popover>
                                                {folderIds.length > 0 && (
                                                    <div className="flex flex-wrap gap-1 mt-0.5">
                                                        {folderIds.map((fid) => {
                                                            const f = flatFolders.find((x) => x.id === fid);
                                                            return (
                                                                <Badge key={fid} variant="outline" className="gap-1 bg-white">
                                                                    📁 {f?.name ?? `#${fid}`}
                                                                    <button
                                                                        onClick={() =>
                                                                            setFolderIds((prev) => prev.filter((x) => x !== fid))
                                                                        }
                                                                        className="ml-1 text-red-500"
                                                                    >
                                                                        <X className="w-3 h-3" />
                                                                    </button>
                                                                </Badge>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Toggle subpastas */}
                                            <div className="flex items-center justify-between text-xs">
                                                <Label className="text-xs">Incluir subpastas recursivamente</Label>
                                                <Switch
                                                    checked={includeSubfolders}
                                                    onCheckedChange={setIncludeSubfolders}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* ─── Domínios fonte (accordion) ───
                                Some quando o grupo é 100% pastas — sem TI, não
                                precisa de domínio. Estado preservado caso o
                                slider volte pra <100%. */}
                            {folderMixPct === 100 ? (
                                <div className="border rounded-lg px-3 py-2.5 bg-violet-50/30 border-violet-200 flex items-center gap-2">
                                    <Info className="w-4 h-4 text-violet-600 shrink-0" />
                                    <p className="text-xs text-violet-900">
                                        <strong>Modo 100% pastas</strong> — Template Intelligence desligado,
                                        domínios fonte não são necessários.
                                    </p>
                                </div>
                            ) : (
                            <div className="border rounded-lg overflow-hidden">
                                <button
                                    type="button"
                                    onClick={() => setDomainSectionOpen((v) => !v)}
                                    className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 transition-colors"
                                >
                                    <div className="flex items-center gap-2 text-left">
                                        <ChevronDown className={`w-4 h-4 transition-transform ${domainSectionOpen ? '' : '-rotate-90'}`} />
                                        <span className="text-sm font-medium">
                                            Domínios fonte <span className="text-gray-400 font-normal">*</span>
                                        </span>
                                        {sourceDomains.length > 0 && (
                                            <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200">
                                                {sourceDomains.length} {sourceDomains.length === 1 ? 'domínio' : 'domínios'}
                                            </Badge>
                                        )}
                                    </div>
                                    {!domainSectionOpen && sourceDomains.length > 0 && (
                                        <span className="text-xs text-gray-500 truncate max-w-[280px] font-mono">
                                            {sourceDomains.slice(0, 2).join(', ')}{sourceDomains.length > 2 && ` +${sourceDomains.length - 2}`}
                                        </span>
                                    )}
                                </button>
                                {domainSectionOpen && (
                                    <div className="border-t p-3 space-y-2">
                                        <p className="text-xs text-gray-500">De onde tirar os templates. Counts são overall (catálogo total).</p>
                                        <div className="relative">
                                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                            <Input
                                                value={domainSearch}
                                                onChange={(e) => setDomainSearch(e.target.value)}
                                                placeholder="Buscar domínio..."
                                                className="pl-7 text-sm"
                                            />
                                        </div>
                                        <div className="border rounded-lg max-h-56 overflow-y-auto bg-white">
                                            {optionsLoading ? (
                                                <p className="p-3 text-sm text-gray-400 flex items-center gap-2">
                                                    <Loader2 className="w-3 h-3 animate-spin" /> carregando…
                                                </p>
                                            ) : visibleDomains.length === 0 ? (
                                                <p className="p-3 text-sm text-gray-400">
                                                    Nenhum domínio {projectFilter.length > 0 ? 'no(s) projeto(s) selecionado(s)' : 'disponível'}.
                                                </p>
                                            ) : (
                                                visibleDomains.map((d) => {
                                                    const hasTemplates = (d.templates_count ?? 0) > 0;
                                                    return (
                                                        <label
                                                            key={d.domain}
                                                            className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer border-b last:border-b-0 ${
                                                                hasTemplates ? 'hover:bg-gray-50' : 'hover:bg-amber-50/50 bg-amber-50/20'
                                                            }`}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={sourceDomains.includes(d.domain)}
                                                                onChange={() => toggleDomain(d.domain)}
                                                            />
                                                            <span className="text-sm flex-1 font-mono">{d.domain}</span>
                                                            {d.kind === 'redirect' && (
                                                                <Badge variant="outline" className="text-[9px] font-normal bg-violet-50 text-violet-700 border-violet-200">
                                                                    redirect
                                                                </Badge>
                                                            )}
                                                            {d.kind === 'language' && (
                                                                <Badge variant="outline" className="text-[9px] font-normal bg-sky-50 text-sky-700 border-sky-200 uppercase">
                                                                    {d.kind_detail || 'idioma'}
                                                                </Badge>
                                                            )}
                                                            <Badge
                                                                variant="outline"
                                                                className={`text-[10px] font-mono ${
                                                                    hasTemplates
                                                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                                        : 'bg-gray-50 text-gray-400 border-gray-200'
                                                                }`}
                                                                title={hasTemplates ? `${d.templates_count} templates no catálogo (overall)` : 'Sem templates no catálogo'}
                                                            >
                                                                {d.templates_count ?? 0} tpls
                                                            </Badge>
                                                            {d.project_name && (
                                                                <Badge variant="outline" className="text-[10px] font-normal max-w-[120px] truncate">
                                                                    {d.project_name}
                                                                </Badge>
                                                            )}
                                                        </label>
                                                    );
                                                })
                                            )}
                                        </div>
                                        {sourceDomains.length > 0 && (
                                            <div className="flex flex-wrap gap-1">
                                                {sourceDomains.map((d) => (
                                                    <Badge key={d} variant="outline" className="gap-1 group font-mono">
                                                        {d}
                                                        <button onClick={() => toggleDomain(d)} className="ml-1 text-red-500">
                                                            <X className="w-3 h-3" />
                                                        </button>
                                                    </Badge>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            )}

                            {/* ─── Páginas Meta destino (accordion) ─── */}
                            <div className="border rounded-lg overflow-hidden">
                                <button
                                    type="button"
                                    onClick={() => setPageSectionOpen((v) => !v)}
                                    className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 transition-colors"
                                >
                                    <div className="flex items-center gap-2 text-left">
                                        <ChevronDown className={`w-4 h-4 transition-transform ${pageSectionOpen ? '' : '-rotate-90'}`} />
                                        <span className="text-sm font-medium">
                                            Páginas Meta destino <span className="text-gray-400 font-normal">*</span>
                                        </span>
                                        {pageIds.length > 0 && (
                                            <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200">
                                                {pageIds.length} {pageIds.length === 1 ? 'página' : 'páginas'}
                                            </Badge>
                                        )}
                                    </div>
                                    {!pageSectionOpen && pageIds.length > 0 && (
                                        <span className="text-xs text-gray-500 truncate max-w-[280px]">
                                            {pageIds.slice(0, 2).map((id) => allPages.find((p) => p.id === id)?.page_name).filter(Boolean).join(', ')}
                                            {pageIds.length > 2 && ` +${pageIds.length - 2}`}
                                        </span>
                                    )}
                                </button>
                                {pageSectionOpen && (
                                    <div className="border-t p-3 space-y-2">
                                        {/* Filtros: categoria + meta_user em popovers compactos */}
                                        <div className="grid grid-cols-2 gap-2">
                                            <Popover open={categoryPopoverOpen} onOpenChange={setCategoryPopoverOpen}>
                                                <PopoverTrigger asChild>
                                                    <Button variant="outline" role="combobox" className="justify-between text-xs h-8 font-normal">
                                                        <span className="truncate">
                                                            {categoryFilter.length === 0
                                                                ? 'Categoria'
                                                                : `${categoryFilter.length} categoria${categoryFilter.length > 1 ? 's' : ''}`}
                                                        </span>
                                                        <ChevronDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="p-0" style={{ width: 'var(--radix-popover-trigger-width)' }} align="start">
                                                    <Command>
                                                        <CommandInput placeholder="Buscar categoria..." />
                                                        <CommandList>
                                                            <CommandEmpty>Nenhuma categoria.</CommandEmpty>
                                                            <CommandGroup>
                                                                {allCategories.map((c) => {
                                                                    const selected = categoryFilter.includes(c.id);
                                                                    return (
                                                                        <CommandItem
                                                                            key={c.id}
                                                                            value={c.name}
                                                                            onSelect={() =>
                                                                                setCategoryFilter((prev) =>
                                                                                    prev.includes(c.id) ? prev.filter((x) => x !== c.id) : [...prev, c.id],
                                                                                )
                                                                            }
                                                                        >
                                                                            <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${
                                                                                selected ? 'bg-violet-600 border-violet-600' : 'border-gray-300'
                                                                            }`}>
                                                                                {selected && <Check className="h-3 w-3 text-white" />}
                                                                            </div>
                                                                            <span>{c.name}</span>
                                                                        </CommandItem>
                                                                    );
                                                                })}
                                                            </CommandGroup>
                                                            {categoryFilter.length > 0 && (
                                                                <div className="border-t p-2">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setCategoryFilter([])}
                                                                        className="text-xs text-gray-500 hover:underline w-full text-left px-2 py-1"
                                                                    >
                                                                        Limpar
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </CommandList>
                                                    </Command>
                                                </PopoverContent>
                                            </Popover>

                                            <Popover open={metaUserPopoverOpen} onOpenChange={setMetaUserPopoverOpen}>
                                                <PopoverTrigger asChild>
                                                    <Button variant="outline" role="combobox" className="justify-between text-xs h-8 font-normal">
                                                        <span className="truncate">
                                                            {metaUserFilter.length === 0
                                                                ? 'Usuário Meta'
                                                                : `${metaUserFilter.length} usuário${metaUserFilter.length > 1 ? 's' : ''}`}
                                                        </span>
                                                        <ChevronDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="p-0" style={{ width: 'var(--radix-popover-trigger-width)' }} align="start">
                                                    <Command>
                                                        <CommandInput placeholder="Buscar usuário..." />
                                                        <CommandList>
                                                            <CommandEmpty>Nenhum usuário.</CommandEmpty>
                                                            <CommandGroup>
                                                                {allMetaUsers.map((mu) => {
                                                                    const selected = metaUserFilter.includes(mu);
                                                                    return (
                                                                        <CommandItem
                                                                            key={mu}
                                                                            value={mu}
                                                                            onSelect={() =>
                                                                                setMetaUserFilter((prev) =>
                                                                                    prev.includes(mu) ? prev.filter((x) => x !== mu) : [...prev, mu],
                                                                                )
                                                                            }
                                                                        >
                                                                            <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${
                                                                                selected ? 'bg-violet-600 border-violet-600' : 'border-gray-300'
                                                                            }`}>
                                                                                {selected && <Check className="h-3 w-3 text-white" />}
                                                                            </div>
                                                                            <span>{mu}</span>
                                                                        </CommandItem>
                                                                    );
                                                                })}
                                                            </CommandGroup>
                                                            {metaUserFilter.length > 0 && (
                                                                <div className="border-t p-2">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setMetaUserFilter([])}
                                                                        className="text-xs text-gray-500 hover:underline w-full text-left px-2 py-1"
                                                                    >
                                                                        Limpar
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </CommandList>
                                                    </Command>
                                                </PopoverContent>
                                            </Popover>
                                        </div>

                                        <div className="relative">
                                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                            <Input
                                                value={pageSearch}
                                                onChange={(e) => setPageSearch(e.target.value)}
                                                placeholder="Buscar página (nome, ID ou usuário Meta)..."
                                                className="pl-7 text-sm"
                                            />
                                        </div>
                                        <div className="border rounded-lg max-h-56 overflow-y-auto bg-white">
                                            {optionsLoading ? (
                                                <p className="p-3 text-sm text-gray-400 flex items-center gap-2">
                                                    <Loader2 className="w-3 h-3 animate-spin" /> carregando…
                                                </p>
                                            ) : visiblePages.length === 0 ? (
                                                <p className="p-3 text-sm text-gray-400">
                                                    Nenhuma página com os filtros atuais.
                                                </p>
                                            ) : (
                                                visiblePages.map((p) => (
                                                    <label
                                                        key={p.id}
                                                        className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={pageIds.includes(p.id)}
                                                            onChange={() => togglePage(p.id)}
                                                        />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="text-sm font-medium text-gray-900 truncate">
                                                                {p.page_name || '(sem nome)'}
                                                            </div>
                                                            <div className="text-[10px] text-gray-500 truncate">
                                                                ID {p.page_id}
                                                                {p.meta_user_name && ` · ${p.meta_user_name}`}
                                                                {p.category_name && ` · ${p.category_name}`}
                                                            </div>
                                                        </div>
                                                        {p.project_ids.length > 0 && (
                                                            <div className="flex flex-wrap gap-0.5 max-w-[180px] justify-end">
                                                                {p.project_ids.slice(0, 2).map((pid) => {
                                                                    const proj = projects.find((x) => x.id === pid);
                                                                    return (
                                                                        <Badge key={pid} variant="outline" className="text-[10px] font-normal">
                                                                            {proj?.name ?? `#${pid}`}
                                                                        </Badge>
                                                                    );
                                                                })}
                                                                {p.project_ids.length > 2 && (
                                                                    <Badge variant="outline" className="text-[10px] font-normal">
                                                                        +{p.project_ids.length - 2}
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        )}
                                                    </label>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Pool counter — só mostra se há domínios escolhidos */}
                            {sourceDomains.length > 0 && (
                                <div className={`rounded-lg p-3 flex items-center gap-3 border ${
                                    previewLoading
                                        ? 'bg-gray-50 border-gray-200'
                                        : previewCount && previewCount.total > 0
                                            ? 'bg-emerald-50 border-emerald-200'
                                            : 'bg-amber-50 border-amber-200'
                                }`}>
                                    {previewLoading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                                            <span className="text-sm text-gray-600">Calculando pool de templates...</span>
                                        </>
                                    ) : previewCount && previewCount.total > 0 ? (
                                        <>
                                            <BarChart3 className="w-4 h-4 text-emerald-600" />
                                            <div className="flex-1">
                                                <p className="text-sm font-medium text-emerald-900">
                                                    Pool disponível: <strong>{previewCount.total}</strong> templates
                                                </p>
                                                <p className="text-xs text-emerald-700 mt-0.5">
                                                    {Object.entries(previewCount.per_domain).map(([d, c], i, arr) => (
                                                        <span key={d}>
                                                            <span className="font-mono">{d}</span>: <strong>{c}</strong>{i < arr.length - 1 ? ' · ' : ''}
                                                        </span>
                                                    ))}
                                                </p>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <AlertCircle className="w-4 h-4 text-amber-600" />
                                            <div className="flex-1">
                                                <p className="text-sm font-medium text-amber-900">
                                                    Pool vazio pros filtros atuais
                                                </p>
                                                <p className="text-xs text-amber-700 mt-0.5">
                                                    Nenhum template com país=<strong>{country}</strong>. Tente um domínio "redirect" (badge violeta).
                                                </p>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {step === 3 && (
                        <div className="grid gap-5">
                            {/* Horários */}
                            <div className="grid gap-2">
                                <Label>
                                    Horários de envio diários *
                                    <span className="text-gray-400 text-xs ml-1">
                                        (horário literal no fuso do grupo — ex.: 07:00 = 07:00 no fuso abaixo)
                                    </span>
                                </Label>
                                <div className="flex gap-2">
                                    <Input
                                        type="time"
                                        step={60}
                                        value={newTime}
                                        onChange={(e) => setNewTime(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTime())}
                                        className="font-mono"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={addTime}
                                        disabled={!newTime || scheduleTimes.includes(newTime)}
                                    >
                                        <Plus className="w-4 h-4" />
                                    </Button>
                                </div>
                                <p className="text-[10px] text-gray-400 -mt-1">
                                    {scheduleTimes.length === 0
                                        ? 'Adicione pelo menos 1 horário.'
                                        : `${scheduleTimes.length} ${scheduleTimes.length === 1 ? 'horário' : 'horários'} configurado${scheduleTimes.length > 1 ? 's' : ''}.`}
                                </p>
                                <div className="flex flex-wrap gap-1">
                                    {scheduleTimes.map((t) => (
                                        <Badge key={t} variant="outline" className="gap-1 font-mono group">
                                            {t}
                                            <button onClick={() => removeTime(t)} className="ml-1 text-red-500 opacity-0 group-hover:opacity-100">
                                                <X className="w-3 h-3" />
                                            </button>
                                        </Badge>
                                    ))}
                                </div>
                            </div>

                            {/* Fuso horário do grupo */}
                            <div className="grid gap-2">
                                <Label>Fuso horário dos envios</Label>
                                <Select value={scheduleTimezone} onValueChange={setScheduleTimezone}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={TZ_AUTO}>Automático (maioria das páginas)</SelectItem>
                                        {/* valor gravado fora da lista curada (ex.: America/Detroit) */}
                                        {scheduleTimezone !== TZ_AUTO
                                            && !TIMEZONE_OPTIONS.some((o) => o.value === scheduleTimezone) && (
                                            <SelectItem value={scheduleTimezone}>{scheduleTimezone}</SelectItem>
                                        )}
                                        {TIMEZONE_OPTIONS.map((o) => (
                                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-[10px] text-gray-400 -mt-1">
                                    {scheduleTimezone === TZ_AUTO
                                        ? 'Automático: o fuso é derivado pela maioria das páginas do grupo (páginas sem geolocalização são ignoradas; fallback São Paulo).'
                                        : 'Os horários acima são interpretados literalmente neste fuso.'}
                                </p>
                            </div>

                            {/* Ranking — só relevante quando há domínios fonte (Template
                                Intelligence). Em modo 100% pastas, esses critérios não se
                                aplicam (templates vêm em round-robin das suas pastas). */}
                            {folderMixPct < 100 ? (
                                <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                                    <div className="grid gap-2">
                                        <Label>Critério de ranking</Label>
                                        <Select value={rankingMetric} onValueChange={(v) => setRankingMetric(v as EsteiraRankingMetric)}>
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="ctr">CTR (taxa de clique)</SelectItem>
                                                <SelectItem value="pageviews">Pageviews</SelectItem>
                                                <SelectItem value="rpm">RPM</SelectItem>
                                                <SelectItem value="ecpm">eCPM</SelectItem>
                                                <SelectItem value="revenue">Revenue</SelectItem>
                                                <SelectItem value="broadcasts">Volume de envios</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="grid gap-2">
                                        <Label>Janela do ranking (dias)</Label>
                                        <Input
                                            type="number" min={1} max={90} value={rankingPeriodDays}
                                            onChange={(e) => setRankingPeriodDays(Number(e.target.value) || 30)}
                                            disabled={useAllTimeRanking}
                                            className={useAllTimeRanking ? 'opacity-50' : ''}
                                        />
                                        <p className="text-[10px] text-gray-400 -mt-1">
                                            {useAllTimeRanking
                                                ? 'Ignorado — usando histórico completo.'
                                                : 'Período do ranking de templates.'}
                                        </p>
                                    </div>
                                    <div className="grid gap-2">
                                        <Label>Top templates considerados</Label>
                                        <Input type="number" min={10} max={500} value={topPoolSize}
                                            onChange={(e) => setTopPoolSize(Number(e.target.value) || 50)} />
                                        <p className="text-[10px] text-gray-400 -mt-1">
                                            Tamanho do pool de candidatos.
                                        </p>
                                    </div>
                                    <div className="grid gap-2">
                                        <Label>Mín. leads alcançados</Label>
                                        <Input type="number" min={0} value={minTotalSent}
                                            onChange={(e) => setMinTotalSent(Number(e.target.value) || 0)} />
                                        <p className="text-[10px] text-gray-400 -mt-1">
                                            Soma de leads que receberam o template. 0 = sem filtro.
                                        </p>
                                    </div>
                                    <div className="grid gap-2">
                                        <Label>Mín. cliques</Label>
                                        <Input type="number" min={0} value={minPageviews}
                                            onChange={(e) => setMinPageviews(Number(e.target.value) || 0)} />
                                        <p className="text-[10px] text-gray-400 -mt-1">
                                            Evita CTR fake em amostras pequenas. 0 = sem filtro.
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="border-t pt-3">
                                    <div className="bg-violet-50/30 border border-violet-200 rounded-lg px-3 py-2.5 flex items-start gap-2">
                                        <Info className="w-4 h-4 text-violet-600 shrink-0 mt-0.5" />
                                        <p className="text-xs text-violet-900 leading-relaxed">
                                            <strong>Modo 100% pastas:</strong> ranking, top pool, mínimos
                                            de leads/cliques e histórico só se aplicam quando há domínios
                                            fonte (Template Intelligence). Aqui, os templates das suas
                                            pastas são usados em round-robin sequencial.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Anti-repeat — relevante em qualquer modo */}
                            <div className="grid grid-cols-2 gap-3">
                                <div className="grid gap-2">
                                    <Label>Não repetir o mesmo template</Label>
                                    <Input type="number" min={0} max={100} value={antiRepeatWindow}
                                        onChange={(e) => setAntiRepeatWindow(Number(e.target.value) || 0)} />
                                    <p className="text-[10px] text-gray-400 -mt-1">
                                        Últimos N envios. 0 = desligado.
                                    </p>
                                </div>
                                <div className="grid gap-2">
                                    <Label>Não repetir o mesmo texto</Label>
                                    <Input type="number" min={0} max={100} value={contentRepeatWindow}
                                        onChange={(e) => setContentRepeatWindow(Number(e.target.value) || 0)} />
                                    <p className="text-[10px] text-gray-400 -mt-1">
                                        Mesmo c/ foto diferente. 0 = desligado.
                                    </p>
                                </div>
                            </div>

                            {/* Histórico completo — só TI */}
                            {folderMixPct < 100 && (
                                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <Label className="text-sm font-medium text-slate-900">
                                                Usar histórico completo
                                            </Label>
                                            <p className="text-xs text-slate-600 mt-0.5">
                                                Ignora a janela de dias e considera todas as métricas históricas dos templates.
                                            </p>
                                        </div>
                                        <Switch checked={useAllTimeRanking} onCheckedChange={setUseAllTimeRanking} />
                                    </div>
                                </div>
                            )}

                            {/* Distribuição entre páginas */}
                            <div className="border rounded-lg p-3 space-y-3">
                                <div>
                                    <Label className="text-sm font-medium">Distribuição entre páginas</Label>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        Como espalhar os templates quando o grupo tem múltiplas páginas.
                                    </p>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setPageTemplateStrategy('synchronized')}
                                        className={`text-left border rounded-lg px-3 py-2 transition-colors ${
                                            pageTemplateStrategy === 'synchronized'
                                                ? 'border-violet-500 bg-violet-50'
                                                : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                    >
                                        <div className="font-medium text-sm">Sincronizada</div>
                                        <div className="text-xs text-gray-500 mt-0.5">
                                            Mesmo template em todas as páginas no horário.
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPageTemplateStrategy('per_page_random')}
                                        className={`text-left border rounded-lg px-3 py-2 transition-colors ${
                                            pageTemplateStrategy === 'per_page_random'
                                                ? 'border-violet-500 bg-violet-50'
                                                : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                    >
                                        <div className="font-medium text-sm">Aleatória por página</div>
                                        <div className="text-xs text-gray-500 mt-0.5">
                                            Cada página recebe um template diferente, sem repetir os anteriores.
                                        </div>
                                    </button>
                                </div>
                            </div>

                            {/* Mix sequencial card/button */}
                            <div className="border rounded-lg p-3 space-y-3">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <Label className="text-sm font-medium">Intercalar tipos sequencial</Label>
                                        <p className="text-xs text-gray-500 mt-0.5">
                                            Força padrão fixo "a cada N cards, 1 texto + botões". Determinístico (diferente da IA, que é estocástica).
                                        </p>
                                    </div>
                                    <Switch
                                        checked={templateTypeMixMode === 'sequential'}
                                        onCheckedChange={(v) => setTemplateTypeMixMode(v ? 'sequential' : 'filter_only')}
                                    />
                                </div>
                                {templateTypeMixMode === 'sequential' && (
                                    <>
                                        {templateTypeFilter !== 'any' && (
                                            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800 flex items-start gap-2">
                                                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                                <span>
                                                    Mix sequencial requer "Tipo de template" = <strong>Todos</strong> (precisa ter cards e textos+botões no pool). Ajuste no step 2 ou desligue essa opção.
                                                </span>
                                            </div>
                                        )}
                                        <div className="grid gap-2">
                                            <Label className="text-xs">
                                                A cada N cards, envia 1 texto + botões
                                            </Label>
                                            <Input
                                                type="number" min={1} max={50}
                                                value={cardsPerButton}
                                                onChange={(e) => setCardsPerButton(Number(e.target.value) || 3)}
                                                placeholder="3"
                                            />
                                            <p className="text-[10px] text-gray-400 -mt-1">
                                                Ex: 3 = padrão "card, card, card, texto+botões, card, card, card, texto+botões..."
                                            </p>
                                        </div>
                                    </>
                                )}
                            </div>

                        </div>
                    )}

                    {step === 4 && (
                        <div className="grid gap-5">
                            {/* URL de destino — única, obrigatória, sobrescreve TUDO */}
                            <div className="grid gap-2">
                                <Label htmlFor="destination-url">
                                    URL de destino <span className="text-red-500">*</span>
                                </Label>
                                <p className="text-xs text-gray-500">
                                    Substitui o link de <strong>todos os botões</strong> e da <strong>foto</strong> (clique no card)
                                    dos templates enviados. Use só a URL base — as UTMs são injetadas
                                    automaticamente no envio.
                                </p>
                                <Input
                                    id="destination-url"
                                    value={destinationUrl}
                                    onChange={(e) => setDestinationUrl(e.target.value)}
                                    placeholder="https://app.twintnews.com/auto-chile"
                                    className={
                                        destinationUrl && !isValidUrl(destinationUrl)
                                            ? 'border-red-300 focus-visible:ring-red-400'
                                            : ''
                                    }
                                />
                                {destinationUrl && !isValidUrl(destinationUrl) && (
                                    <p className="text-[11px] text-red-600 flex items-center gap-1">
                                        <AlertCircle className="w-3 h-3" />
                                        URL inválida — precisa começar com <code>http://</code> ou <code>https://</code>.
                                    </p>
                                )}
                                {isValidUrl(destinationUrl) && (
                                    <p className="text-[11px] text-emerald-600 flex items-center gap-1">
                                        <Check className="w-3 h-3" />
                                        Vai sobrescrever foto + todos os botões com essa URL.
                                    </p>
                                )}
                            </div>

                            {/* Segmentação de audiência — quem recebe */}
                            <div className="grid gap-2 pt-3 border-t">
                                <Label className="text-sm font-medium">🎯 Quem recebe</Label>
                                <div className="flex flex-col gap-1.5">
                                    {AUDIENCE_WINDOW_OPTIONS.map((o) => (
                                        <label key={o.v} className="flex items-center gap-2 cursor-pointer text-sm">
                                            <input
                                                type="radio"
                                                name="audience-window"
                                                className="h-4 w-4"
                                                checked={audienceWindow === o.v}
                                                onChange={() => setAudienceWindow(o.v)}
                                            />
                                            <span>{o.label}</span>
                                            <span className="text-[11px] text-gray-400">· {o.hint}</span>
                                        </label>
                                    ))}
                                </div>
                                {AUDIENCE_NEEDS_TEMPLATE.includes(audienceWindow) && !metaSyncEnabled && !fallbackFolderId && (
                                    <p className="text-[11px] text-amber-600 flex items-center gap-1">
                                        <AlertCircle className="w-3 h-3" />
                                        Leads fora da janela 24h (e fora dos 7 dias do anúncio) recusam envio raw (<code>#10</code>). Ligue o Sync com Meta ou configure um fallback aprovado.
                                    </p>
                                )}
                                <label className="flex items-center gap-2 cursor-pointer text-sm mt-1 pt-2 border-t border-dashed">
                                    <input
                                        type="checkbox"
                                        className="h-4 w-4"
                                        checked={skipLeadsInFlow}
                                        onChange={(e) => setSkipLeadsInFlow(e.target.checked)}
                                    />
                                    <span>Pular leads que estão num flow ativo</span>
                                </label>
                                <p className="text-[11px] text-gray-500">
                                    Quando <b>ligado</b>: não envia pra leads no meio de um flow (executando, esperando clique ou em delay).
                                </p>
                            </div>

                            {/* Sync Meta + retry */}
                            <div className="grid gap-2 pt-3 border-t">
                                <div className="flex items-center gap-2">
                                    <input
                                        id="meta-sync-enabled"
                                        type="checkbox"
                                        className="h-4 w-4"
                                        checked={metaSyncEnabled}
                                        onChange={(e) => setMetaSyncEnabled(e.target.checked)}
                                    />
                                    <Label htmlFor="meta-sync-enabled" className="cursor-pointer">
                                        Sync com Meta (usar template UTILITY aprovado)
                                    </Label>
                                </div>
                                <p className="text-[11px] text-gray-500">
                                    Quando <b>ligado</b>: templates da pasta são submetidos pra aprovação Meta e envio usa <code>messaging_type=UTILITY</code> (fura janela 24h).
                                    Quando <b>desligado</b>: envio raw legado (pode dar erro <code>#10</code> fora da janela).
                                </p>

                                {/* Fallback de envio. Pasta de templates UTILITY aprovados. */}
                                <div className="grid gap-1.5 mt-2 pt-2 border-t border-dashed">
                                        <Label className="text-xs flex items-center gap-1">
                                            🛟 Fallback de envio
                                        </Label>
                                        <p className="text-[11px] text-gray-500">
                                            Pasta de templates <b>UTILITY aprovados</b>. Quando o envio primário raw falhar (ex.: <code>#10</code> fora da janela), reenvia o lead com um template aprovado aleatório dessa pasta. No retry de erros, manda só o aprovado. Vazio = sem fallback.
                                        </p>
                                        <Popover open={fallbackPickerOpen} onOpenChange={setFallbackPickerOpen}>
                                            <PopoverTrigger asChild>
                                                <Button
                                                    variant="outline"
                                                    role="combobox"
                                                    className="w-full justify-between font-normal bg-white"
                                                >
                                                    <span className="truncate">
                                                        {fallbackFolderId == null
                                                            ? 'Sem fallback'
                                                            : `📁 ${flatFolders.find((x) => x.id === fallbackFolderId)?.name ?? `#${fallbackFolderId}`}`}
                                                    </span>
                                                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent
                                                className="p-0"
                                                style={{ width: 'var(--radix-popover-trigger-width)' }}
                                                align="start"
                                            >
                                                <Command>
                                                    <CommandInput placeholder="Buscar pasta..." />
                                                    <CommandList className="max-h-[320px] overflow-y-auto">
                                                        <CommandEmpty>
                                                            Nenhuma pasta criada ainda — vá em "Biblioteca de modelos".
                                                        </CommandEmpty>
                                                        <CommandGroup>
                                                            <CommandItem
                                                                value="__sem_fallback__"
                                                                onSelect={() => { setFallbackFolderId(null); setFallbackPickerOpen(false); }}
                                                            >
                                                                <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${
                                                                    fallbackFolderId == null ? 'bg-violet-600 border-violet-600' : 'border-gray-300'
                                                                }`}>
                                                                    {fallbackFolderId == null && <Check className="h-3 w-3 text-white" />}
                                                                </div>
                                                                <span className="flex-1 truncate text-gray-500">Sem fallback</span>
                                                            </CommandItem>
                                                            {flatFolders
                                                                .filter((f) => !folderIds.includes(f.id))
                                                                .map((f) => {
                                                                    const selected = fallbackFolderId === f.id;
                                                                    return (
                                                                        <CommandItem
                                                                            key={f.id}
                                                                            value={f.name}
                                                                            onSelect={() => { setFallbackFolderId(selected ? null : f.id); setFallbackPickerOpen(false); }}
                                                                        >
                                                                            <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${
                                                                                selected ? 'bg-violet-600 border-violet-600' : 'border-gray-300'
                                                                            }`}>
                                                                                {selected && <Check className="h-3 w-3 text-white" />}
                                                                            </div>
                                                                            <span className="flex-1 truncate">{f.name}</span>
                                                                            <span className="text-[10px] text-gray-400 ml-2">
                                                                                {f.template_count} {f.template_count === 1 ? 'tpl' : 'tpls'}
                                                                            </span>
                                                                        </CommandItem>
                                                                    );
                                                                })}
                                                        </CommandGroup>
                                                    </CommandList>
                                                </Command>
                                            </PopoverContent>
                                        </Popover>
                                </div>

                                <div className="grid grid-cols-2 gap-2 mt-1">
                                    <div>
                                        <Label className="text-xs">Retry — códigos de erro</Label>
                                        <div className="flex items-center gap-3 mt-1">
                                            {([
                                                { code: '10', label: '#10 fora janela 24h' },
                                                { code: '551', label: '#551 lead unavailable' },
                                            ] as const).map(({ code, label }) => {
                                                const selected = retryErrorCodesRaw
                                                    .split(',').map((s) => s.trim()).includes(code);
                                                return (
                                                    <label key={code} className="flex items-center gap-1.5 text-xs cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            className="h-3.5 w-3.5"
                                                            checked={selected}
                                                            onChange={(e) => {
                                                                const cur = retryErrorCodesRaw
                                                                    .split(',').map((s) => s.trim()).filter(Boolean);
                                                                if (e.target.checked) {
                                                                    if (!cur.includes(code)) cur.push(code);
                                                                } else {
                                                                    const idx = cur.indexOf(code);
                                                                    if (idx >= 0) cur.splice(idx, 1);
                                                                }
                                                                setRetryErrorCodesRaw(cur.join(','));
                                                            }}
                                                        />
                                                        {label}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                        <p className="text-[10px] text-gray-400 mt-0.5">Desmarque tudo pra desativar retry.</p>
                                    </div>
                                    <div>
                                        <Label className="text-xs">Retry — offset (minutos)</Label>
                                        <select
                                            className="w-full mt-1 h-9 border rounded-md px-2 text-xs font-mono"
                                            value={retryOffsetMinutes ?? 45}
                                            onChange={(e) => setRetryOffsetMinutes(parseInt(e.target.value, 10))}
                                        >
                                            <option value={15}>15 minutos</option>
                                            <option value={30}>30 minutos</option>
                                            <option value={45}>45 minutos</option>
                                            <option value={60}>60 minutos</option>
                                        </select>
                                        <p className="text-[10px] text-gray-400 mt-0.5">Espera N min antes de tentar de novo.</p>
                                    </div>
                                </div>
                            </div>

                            {/* UTM templates */}
                            <div className="grid gap-2 pt-2 border-t">
                                <Label>UTMs (rastreamento)</Label>
                                <p className="text-xs text-gray-500">
                                    Substitua os placeholders pelos valores reais no momento do envio.
                                </p>
                                <p className="text-[10px] text-gray-400">
                                    Disponíveis: <code className="bg-gray-100 px-1 rounded">{PLACEHOLDER_HINT}</code>
                                </p>
                                <div className="grid grid-cols-2 gap-2 mt-1">
                                    <div>
                                        <Label className="text-xs">utm_source</Label>
                                        <Input className="font-mono text-xs" value={utmSourceTpl}
                                            onChange={(e) => setUtmSourceTpl(e.target.value)} />
                                    </div>
                                    <div>
                                        <Label className="text-xs">utm_medium</Label>
                                        <Input className="font-mono text-xs" value={utmMediumTpl}
                                            onChange={(e) => setUtmMediumTpl(e.target.value)} />
                                    </div>
                                    <div>
                                        <Label className="text-xs">utm_campaign</Label>
                                        <Input className="font-mono text-xs" value={utmCampaignTpl}
                                            onChange={(e) => setUtmCampaignTpl(e.target.value)} />
                                    </div>
                                    <div>
                                        <Label className="text-xs">utm_content</Label>
                                        <Input className="font-mono text-xs" value={utmContentTpl}
                                            onChange={(e) => setUtmContentTpl(e.target.value)} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer com navegação */}
                <DialogFooter className="px-6 py-4 border-t bg-gray-50/50 sm:justify-between gap-2">
                    <div className="flex items-center gap-2">
                        {step > 1 && (
                            <Button
                                variant="outline"
                                onClick={() => setStep((step - 1) as Step)}
                                disabled={submitMutation.isPending}
                            >
                                <ChevronLeft className="w-4 h-4 mr-1" /> Voltar
                            </Button>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {step < 4 ? (
                            <Button
                                onClick={() => setStep((step + 1) as Step)}
                                disabled={!canGoNext || submitMutation.isPending}
                                className="bg-violet-600 hover:bg-violet-700 text-white"
                            >
                                Próximo <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                        ) : (
                            <Button
                                onClick={() => submitMutation.mutate()}
                                disabled={!allValid || submitMutation.isPending}
                                className="bg-violet-600 hover:bg-violet-700 text-white"
                            >
                                {submitMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                {isEdit ? 'Salvar alterações' : 'Criar grupo'}
                            </Button>
                        )}
                    </div>
                </DialogFooter>
            </DialogContent>

            {/* Dialog "Ver templates" das pastas selecionadas */}
            <Dialog open={foldersPreviewOpen} onOpenChange={setFoldersPreviewOpen}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Templates da pasta selecionada</DialogTitle>
                        <DialogDescription>
                            Preview da primeira pasta. Esteira em modo round-robin
                            usa todos os templates de todas as pastas escolhidas.
                        </DialogDescription>
                    </DialogHeader>
                    {previewTemplates.length === 0 ? (
                        <p className="text-sm text-gray-500 py-4">
                            Pasta vazia. Adicione templates antes de criar a esteira.
                        </p>
                    ) : (
                        <div className="grid grid-cols-2 gap-3 mt-2">
                            {previewTemplates.slice(0, 20).map((t) => (
                                <div key={t.id} className="border rounded-lg p-2 bg-white">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-[11px] font-medium truncate">{t.name}</span>
                                        <Badge variant="outline" className="text-[9px]">
                                            {t.template_type === 'generic' ? 'Card' : 'Botão'}
                                        </Badge>
                                    </div>
                                    {t.image_url && (
                                        <div className="aspect-[1.91/1] bg-gray-100 overflow-hidden rounded mb-1">
                                            <img
                                                src={t.image_url}
                                                alt=""
                                                loading="lazy"
                                                className="w-full h-full object-cover"
                                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                            />
                                        </div>
                                    )}
                                    {t.title && <p className="text-[11px] font-semibold line-clamp-1">{t.title}</p>}
                                    {t.subtitle && <p className="text-[10px] text-gray-600 line-clamp-1">{t.subtitle}</p>}
                                </div>
                            ))}
                            {previewTemplates.length > 20 && (
                                <p className="col-span-2 text-[10px] text-center text-gray-400">
                                    + {previewTemplates.length - 20} templates não mostrados
                                </p>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </Dialog>
    );
}
